/**
 * audit-display — 审计/操作日志「字段变更 diff」统一显示模块（前端共享）
 *
 * 一套日志显示规则供两处共用：① 系统设置「操作日志」（全局 audit log，AdminAuditLogResponse）；
 * ② 书籍详情「日志 tab」（book operation log，BookOperationLogItem）。两者 before/after 快照同形，
 * 显示规则统一在此：字段名→中文标签、enum 码→中文、`*_id` 值→后端 field_refs 解析的业务名、对象逐行、长文本截断。
 *
 * 动作 action→中文/类型 走 `~/lib/audit-action-meta`（humanizeAuditAction / auditTypeChip），本模块只管「字段 diff」。
 */

import { useState } from 'react';
import { type AuditActionType, humanizeAuditAction } from '~/lib/audit-action-meta';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { BookCover } from '~/ui/BookCover';
import { Dialog, DialogBody } from '~/ui/Dialog';

/**
 * 字段名 → 友好中文标签。before/after 快照里出现的 key 在此查表展示；未命中退回原始 field 名。
 * 覆盖**全量 audit 发射点**（28 个 service/route）before/after 快照可触达的字段宇宙——
 * 由 audit 字段全量枚举（见 docs/plans/2026-06-25-audit-field-friendly-coverage.md）得出，
 * 不再「打地鼠」逐个补：新增字段若进入 audit 快照，应同步在此登记。嵌套对象的叶子键（如 cascaded
 * 里的 payment_method / is_active）也复用本表（AuditDiffValue 递归渲染时按叶子 key 查表）。
 */
export const FIELD_LABELS: Record<string, string> = {
  price_per_1k_chars: '千字单价 (COIN)',
  updated_at: '更新时间',
  updated_by: '最后修改人',
  status: '状态',
  published_at: '发布时间',
  account_status: '账号状态',
  hit_rule: '命中规则',
  langs: '多语言覆盖',
  refund_status: '退款状态',
  handler: '处理人',
  reason: '原因',
  endpoint_url: '回调地址',
  events: '订阅事件',
  note: '运营备注',
  is_internal_note: '是否内部备注',
  // *_id 对象字段（值经 field_refs 解析为业务名，标签同步友好）
  app_id: '所属 App',
  covered_since: '收入覆盖起始日期',
  verified_at: '资格核验时间',
  qualification_evidence: '资格核验证据',
  evidence_digest: '核验证据摘要',
  risk_rule_id: '风控规则',
  sanctioned_at: '处分时间',
  target_app_id: '目标 App',
  // 采集来源 identity（仅 root 看得到这些行，非 root 已在 BooksPage 侧从 diff 剔除）——
  // 没有这两个 label 时 `FIELD_LABELS[k] ?? k` 会退化成裸 snake_case，运营读不懂
  source_provider: '内容来源渠道',
  source_external_id: '来源外部书籍 ID',
  source_app_id: '复制源 App',
  novel_id: '书籍',
  // dd#970 后新写入用 group_id / source_group_id / source_novel_id；
  // 旧两键保留 —— admin_audit_logs 是 append-only，历史行里仍是那两个 key，删了就退化成裸 snake_case
  group_id: '书组',
  source_group_id: '源书组',
  source_novel_id: '源书籍',
  group_root_id: '书组',
  source_root_id: '源书组',
  chapter_id: '章节',
  target_chapter_id: '发布目标章节',
  queue_target_chapter_id: '队列目标章节',
  published_chapter_ids: '同步发布章节',
  user_id: '用户',
  category_id: '分类',
  category_hint: '主属分类',
  tag_id: '标签',
  tags: '标签',
  banner_id: 'Banner',
  feedback_id: '反馈工单',
  payment_page_id: '支付页',
  page_id: '关联支付页',
  product_metadata_id: '商品',
  push_template_id: '推送模板',
  grant_id: '发放记录',
  refund_id: '退款单',
  last_edited_by_admin_id: '最后编辑人',
  updated_by_admin_id: '计划修改人',
  enqueued_by_admin_id: '入队操作人',
  // i18n 多语言字段（对象逐 locale 渲染）
  answer_i18n: '答案 (i18n)',
  question_i18n: '问题 (i18n)',
  status_reason_i18n: '下架原因 (i18n)',
  name_i18n: '名称 (i18n)',
  title_i18n: '标题 (i18n)',
  body_i18n: '正文 (i18n)',
  recommendation_text_i18n: '书架推荐语 (i18n)',
  target_name_i18n: '锚点标签名 (i18n)',
  // 通用 / 标识
  title: '标题',
  author_display: '作者',
  cover_url: '封面',
  occurred_at: '实际操作时间',
  summary: '操作说明',
  total_chapters: '章节总数',
  total_words: '总字数',
  occurred_between: '实际操作时间范围（无精确时间记录）',
  from: '开始',
  to: '结束',
  recorded_at: '日志记录时间',
  backfilled: '历史补录',
  evidence: '证据来源',
  fingerprint: '事实摘要',
  source: '操作来源',
  name: '名称',
  nickname: '昵称',
  email: '邮箱',
  locale: '语言版本',
  language_code: '语言代码',
  slug: '标识 slug',
  new_slug: '新标识 slug',
  source_slug: '源 slug',
  section_id: '栏目标识',
  device_id: '设备标识',
  // 状态 / 开关（boolean 走通用是/否渲染）
  is_active: '启用状态',
  is_free: '是否免费',
  is_listed: '是否上架',
  enabled: '启用状态',
  deleted: '已物理删除',
  deleted_at: '软删时间',
  auto_translated: '自动翻译',
  override_of_global: '覆盖全局默认栏',
  // 已退役字段仍需标签：历史 audit 记录里存着它，删掉标签会让旧日志退化成裸 snake_case。
  // 语义按其真实行为写——true = 禁用 App 内购 (IAP)、只保留三方通道，**不是**「强制切换到三方支付」。
  force_third_party_payment: '禁用应用内支付 (IAP)（已退役）',
  // 同上：ADM-APP-S009 退役后这两列已无 API surface，但**本次退役之前**写下的 create_app /
  // update_app / copy_app 审计 diff 里存着它们（旧 diffApp 含这两键）。删标签 → 那些旧日志
  // 按 `FIELD_LABELS[k] ?? k` 退化成裸 snake_case，运营读不懂历史记录。
  is_iap_enabled: '应用内支付 (IAP) 开关（已退役）',
  is_stripe_enabled: '第三方支付 (Stripe) 开关（已退役）',
  dnd_enabled: '勿扰开关',
  dnd_start: '勿扰开始',
  dnd_end: '勿扰结束',
  prefs: '通知偏好',
  in_app: '站内',
  push: '推送',
  // 封禁 / 用户
  banned_reason: '封禁原因',
  banned_at: '封禁时间',
  // 书籍 / 章节
  // 书组级字段（dd#970 迁入 novel_groups，经 update_book_group 落 audit）
  cn_title: '中文识别名',
  original_title: '原作书名',
  original_author: '原作者',
  original_locale: '原作语言',
  serial_status: '连载状态',
  free_chapter_count: '免费章节数',
  length_class: '篇幅',
  // ADM-BOK-S024（update_novel / create_novel / create_novel_language diff）
  edition_label: '改写版本',
  price_level: '价格档',
  is_novel_purchase_enabled: '书籍买断启用',
  limited_free_enabled: '本书免费',
  is_recommended: '推荐标记',
  chapter_index: '章节序号',
  word_count: '字数',
  cost: '解锁价格',
  cost_default_per_kw: '千字默认单价',
  content_r2_key: '正文存储键',
  content_sha256: '正文哈希',
  content_version: '正文版本号',
  snapshot_version: '线上快照版本',
  target_snapshot_version: '目标章节线上快照版本',
  draft_revision_seq: '草稿修订分配序号',
  base_snapshot_version: '草稿基线版本',
  draft_content_version: '草稿修订版本',
  has_pending_draft: '存在待重发布草稿',
  content_changed: '正文发生变化',
  target_published_at: '目标章节发布时间',
  published_count: '本次发布章节数',
  latest_chapter_index: '最新发布章节序号',
  scheduled_publish_at: '定时发布时间',
  scheduled_at: '队列计划时间',
  queue_status: '队列状态',
  queue_scheduled_at: '队列计划时间',
  attempts: '执行次数',
  last_error: '最后错误',
  enqueued_by: '入队操作人',
  enqueued_at: '入队时间',
  // 支付 / 付费墙 / 商品
  rewarded_ad_config: '激励广告默认配置',
  rewarded_ad_language_configs: '激励广告语言配置',
  rewarded_ad_units: '激励广告单元',
  bonus_reward: '广告任务 BONUS 奖励',
  task_daily_limit: '广告任务每日名额',
  chapter_daily_limit: '广告解锁每日章节上限',
  registration_day_threshold: '注册日龄门槛',
  ad_unit_id: '原生广告单元 ID',
  ssv_ad_unit_id: 'SSV 广告单元 ID',
  placement: '用途',
  payment_method: '支付方式',
  icon_asset_id: '支付方式图标资产',
  icon_background_color: '支付方式选项底色',
  // 通道策略 diff 的外层包裹键（A8 §7.8.2 / §7.8.4 的有序成员集合）
  payment_methods: '支付方式成员集合',
  // copy_app 审计 after 里 AB §5 复制出来的策略组快照（组 + 有序成员），缺标签会渲染成裸 snake_case
  payment_method_policies: '通道策略组',
  scenario: '支付场景',
  sort_order: '排序',
  display_order: '排序',
  adapty_placement_id: 'Adapty Placement',
  product_type: '商品类型',
  subscription_period: '订阅周期',
  vendor_product_id: '渠道商品标识',
  access_level_id: 'Access Level 标识',
  // iap/stripe_product_id：#264 起折叠为单 access_level_id；保留标签供历史 audit 记录渲染
  iap_product_id: 'IAP product_id',
  stripe_product_id: 'Stripe product_id',
  cascaded_app_payment_methods: '级联软删支付渠道',
  // 页内上架集合（A8 §7.9 / §6.4 / §6.5 / §7.4）：整组替换快照与停用 / 软删 / 删页级联硬删的集合行
  product_orders: '上架商品集合',
  cascaded_product_orders: '级联删除的上架商品',
  // 集合行所属页面的第二段：null = scenario 页，非 null = 该运营支付活动自己的页
  offer_id: '所属活动',
  // 运营弹窗落点改值时被连带同步为同值的其它渠道行（单 placement 不变量，A2 §6.3）
  synced_siblings: '连带同步的其它渠道',
  // 活动 / 资格
  starts_at: '活动开始时间',
  ends_at: '活动结束时间',
  expires_at: '过期时间',
  eligibility: '资格受众',
  // App 配置
  region_code: '地区代码',
  region_codes: '投放区域',
  allowed_languages: '支持语言',
  default_chapter_price_per_kw: '默认每千字价格',
  third_party_bonus_ratio: '第三方加赠比例',
  task_center_rewards_version: '任务奖励版本',
  app_link_scheme: 'App link scheme',
  // 钱包 / BONUS
  amount: '发放金额',
  bonus_balance: 'BONUS 余额',
  bonus_amount: '赠送金币数',
  // STRIPE 产品行写入时按 IAP 基线 + App 比例派生的 bonus 下限（A8 §3 审计须记）
  derived_minimum_bonus_amount: '派生赠送下限',
  coin_amount: '金币数量',
  bonus_expire_days: '赠送金币有效天数',
  // 订阅改为每计费周期一次性发放后该列冻结、不再写入；保留标签供历史审计行展示
  daily_grant_amount: '每日发放数量',
  subscription_effect: '订阅效果',
  // 推送 / 模板 / Banner
  template_slug: '推送模板',
  category: '分类',
  target_scope: '投放范围',
  target_type: '跳转目标类型',
  target_value: '跳转目标值',
  open_mode: 'web 打开方式',
  task_type: '任务类型',
  payload_keys: '载荷字段',
  image_r2_key: '图片存储键',
  // 推荐 / 标签清理
  strategy_key: '推荐策略',
  reading_preferences: '男女频白名单',
  sources: '被合并的源标签',
  merged_source_count: '合并源标签数',
  affected_novels: '受影响书籍数',
  // 风控
  condition_value: '阈值',
  // 反馈
  content_len: '回复字符数',
  original_content_len: '原文字符数',
  reason_locale_count: '原因文案语言数',
  // 导出
  app_scope: '导出口径',
  range: '时间窗',
  row_count: '导出行数',
  // 成员
  role: '角色',
};

/**
 * 字段值枚举 → 友好中文（diff 值展示）：status 等 enum 码不裸露（DELISTED → 下架）。
 * 按 field 名查表（值仍以小字 mono 保留原码，便于对账）。`*_id` 字段值（UUID → 业务名）走后端 field_refs，不在此。
 */
export const FIELD_VALUE_LABELS: Record<string, Record<string, string>> = {
  status: {
    banned: '封禁',
    observed: '风控观察',
    // 书籍可见性（book-visibility，字段名 status）
    LISTED: '上架',
    DELISTED: '下架',
    HIDDEN: '隐藏',
    // App / 商品 / 邀请（小写 active/disabled/pending）
    active: '启用',
    disabled: '停用',
    pending: '待激活',
    // 章节
    PUBLISHED: '已发布',
    DRAFT: '草稿',
    SCHEDULED: '定时发布',
    // 用户封禁/解封（user-moderation，字段名 status，大写枚举）
    BANNED: '封禁',
    ACTIVE: '正常',
    // 反馈工单（feedback：OPEN/IN_PROGRESS/REPLIED/RESOLVED/CLOSED）
    OPEN: '待处理',
    IN_PROGRESS: '处理中',
    REPLIED: '已回复',
    RESOLVED: '已解决',
    CLOSED: '已关闭',
    // 推送任务 / 导出（PENDING/SENDING/COMPLETED/FAILED/CANCELLED）
    PENDING: '待处理',
    SENDING: '发送中',
    COMPLETED: '已完成',
    FAILED: '失败',
    CANCELLED: '已取消',
  },
  account_status: { active: '正常', banned: '封禁', disabled: '停用' },
  refund_status: {
    requested: '已申请',
    approved: '已批准',
    rejected: '已拒绝',
    completed: '已完成',
  },
  // 成员角色
  role: { root: '超级管理员', admin: '管理员', operator: '运营', viewer: '只读' },
  // 支付场景（app_payment_methods.scenario / payment_pages.slug 闭集）
  scenario: {
    charge_center: '充值中心',
    chapter_unlock: '章节解锁',
    member_center: '会员中心',
    reading_pass: '小时阅读卡',
    novel_purchase: '书籍买断',
    limited_offer: '限时优惠',
    new_user_offer: '新人优惠',
    operational_popup: '运营弹窗',
    first_purchase_offer: '首充优惠',
    payment_exit_retention: '支付挽留',
    insufficient_balance_offer: '余额不足优惠',
  },
  // payment_method 只登记结算通道层（`app_payment_methods` / `product_metadata` 的 `IAP` / `STRIPE`，70 §3）。
  // 支付方式层（策略成员与图标设置 diff 里的 `STRIPE_*` 与同步取值）的展示名属 `system_payment_methods.admin_label`，
  // 70 §3 反模式禁止 admin 另维护；审计页跨 App、读不到按 App 求值的 §7.8.8 可选列表，故按原始取值展示。
  payment_method: {
    IAP: '应用内购买 (IAP)',
    STRIPE: 'Stripe',
  },
  // 活动资格受众
  eligibility: { all: '全部用户', new_user: '新用户' },
  // 商品
  product_type: { CONSUMABLE: '消耗型', SUBSCRIPTION: '订阅型' },
  subscription_period: { WEEKLY: '周', MONTHLY: '月', QUARTERLY: '季', YEARLY: '年' },
  // 审计记的是存储值：存量 COIN_DAILY_BONUS 与 COIN_PERIOD_GRANT 同一效果（70 §2），展示同名
  subscription_effect: {
    COIN_PERIOD_GRANT: '周期发币',
    COIN_DAILY_BONUS: '周期发币',
    VIP_READING: 'VIP 畅读',
  },
  // 连载状态（serial_status，与 status 的 COMPLETED 语义不同，故独立表）
  serial_status: { ONGOING: '连载中', COMPLETED: '已完结', HIATUS: '暂停' },
  // 书架推荐策略
  strategy_key: { weekly_featured: '本周精选', for_you: '为你推荐', new_releases: '新书上架' },
  // 男女频白名单（数组元素，AuditDiffValue 数组分支按本表逐元素友好化）
  reading_preferences: { MALE_CHANNEL: '男频', FEMALE_CHANNEL: '女频', NO_PREFERENCE: '不限' },
  // 反馈 / 推送 分类（两域并集）
  category: {
    PAYMENT: '支付',
    CONTENT: '内容',
    BUG: '故障',
    SUGGESTION: '建议',
    ACCOUNT: '账号',
    OTHER: '其他',
    OFFER: '营销优惠',
    NOVEL_UPDATE: '章节更新',
    NOVEL_DELISTED: '书籍下架',
    FEEDBACK: '反馈回复',
    TASK: '任务',
    SUBSCRIPTION: '订阅',
  },
  // 推送模板 slug：覆盖 PUSH_TEMPLATE_REGISTRY 全集（audit-display.test 对账），另留历史 slug
  template_slug: {
    subscription_started: '订阅开始',
    subscription_started_with_bonus: '订阅开始（含赠币）',
    subscription_renewed: '订阅续费',
    subscription_renewed_with_bonus: '订阅续费（含赠币）',
    subscription_renewal_remind: '续费提醒',
    payment_settled: '权益到账',
    payment_settled_with_bonus: '权益到账（含赠币）',
    reading_pass_card_settled: '阅读卡到账',
    reading_pass_free_trial_claim_remind: '免费体验卡领取提醒',
    reading_pass_free_trial_activate_remind: '免费体验卡激活提醒',
    admin_user_notice: '单用户服务通知',
    chapter_update: '章节更新',
    novel_delisted: '书籍下架',
    novel_recommend: '书籍推荐',
    feedback_replied: '反馈回复',
    feedback_reply: '反馈回复',
    task_reward: '任务奖励',
    offer_promo: '营销优惠',
    signin_remind: '签到提醒',
    topup_promo: '充值促销',
    user_recall: '用户召回',
  },
  // 推送任务 / Banner 投放
  task_type: {
    BOOK_DELIST_NOTICE: '书籍下架通知',
    BOOK_RECOMMEND: '书籍推荐',
    CHAPTER_UPDATE: '章节更新',
    SIGNIN_REMIND: '签到提醒',
    SUBSCRIPTION_RENEWAL_REMIND: '续费提醒',
    SUBSCRIPTION_RENEWAL_SUCCESS: '续费成功',
    SUPPORT_REPLY: '客服回复',
    TOPUP_PROMO: '充值促销',
    USER_RECALL: '用户召回',
  },
  target_scope: { all_apps: '全部 App', app: '指定 App' },
  target_type: { book: '书籍', offer: '活动', web: '网页' },
  open_mode: { in_app: '内置浏览器', external: '系统浏览器' },
  // 仪表盘导出时间窗
  range: { '24h': '近 24 小时', '7d': '近 7 天', '30d': '近 30 天', '90d': '近 90 天' },
};

/** 字段级变更：从 before/after 两对象按 key 求并集 diff。 */
export type AuditFieldDiff = { field: string; before: unknown; after: unknown };

/** 超大快照保护：diff 表最多渲染这么多字段行（防异常超长 before/after 撑爆）。 */
const MAX_DIFF_FIELDS = 60;

function asObject(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * 由 before/after 推导字段级 diff：遍历 key 并集，逐 key 取值，仅保留两侧不等的 key。
 * 入参 unknown 容错（两个 DTO 的 before/after 类型不同：audit=Record|null、book=unknown）；非对象 → 视作空。
 * 均空时返回空数组 → 行不显示『N 项变更』、展开区按 action 显兜底文案。最多 MAX_DIFF_FIELDS 行。
 */
/**
 * 只作写入时上下文、不是行状态的审计键（只写在 after 里）：不计入字段 diff，否则每次写都多一行「— → 值」假变更。
 * derived_minimum_bonus_amount：STRIPE 行写入时的派生 bonus 下限（A8 STRIPE 行更新「记录派生下限」）。
 */
const AUDIT_CONTEXT_ONLY_KEYS = new Set(['derived_minimum_bonus_amount', '_operation']);

export function deriveAuditDiff(before: unknown, after: unknown): AuditFieldDiff[] {
  const b = asObject(before);
  const a = asObject(after);
  if (!b && !a) return [];
  const bo = b ?? {};
  const ao = a ?? {};
  const keys = Array.from(new Set([...Object.keys(bo), ...Object.keys(ao)])).filter(
    (key) => !AUDIT_CONTEXT_ONLY_KEYS.has(key),
  );
  return keys
    .map((field) => ({ field, before: bo[field], after: ao[field] }))
    .filter((d) => safeStringify(d.before) !== safeStringify(d.after))
    .slice(0, MAX_DIFF_FIELDS);
}

/** 长文本截断 + 内联展开/收起（limit=56）；stopPropagation 防冒泡触发行折叠。 */
function Truncatable({ text, limit = 56 }: { text: string; limit?: number }) {
  const [open, setOpen] = useState(false);
  if (text.length <= limit) return <span style={{ wordBreak: 'break-word' }}>{text}</span>;
  return (
    <span style={{ wordBreak: 'break-word' }}>
      {open ? text : `${text.slice(0, limit)}…`}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        style={{
          padding: '0 4px',
          height: 16,
          marginLeft: 4,
          fontSize: 10,
          textDecoration: 'none',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {open ? '收起' : '展开'}
      </button>
    </span>
  );
}

/** 仅将合法封面资产路径交给既有鉴权代理，不向历史快照中的任意外链发图片请求。 */
function AuditCoverPreview({ value }: { value: string }) {
  const [open, setOpen] = useState(false);
  let path = value;
  try {
    if (/^https?:\/\//.test(value)) path = new URL(value).pathname;
  } catch {
    path = '';
  }
  const safePath = /^\/api\/v1\/static\/covers\/[0-9a-f-]{36}\/cover\.png$/.test(path)
    ? path
    : null;
  if (!safePath) return <Truncatable text={value} />;
  return (
    <div className="vstack" style={{ gap: 'var(--gap)' }}>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ height: 'auto', width: 'fit-content' }}
        aria-label="预览历史封面"
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
      >
        <BookCover src={safePath} alt="日志快照封面" width={64}>
          <span>图片不可用或加载中</span>
        </BookCover>
      </button>
      <Truncatable text={value} />
      {open && (
        <Dialog title="日志封面预览" onClose={() => setOpen(false)} width={440}>
          <DialogBody>
            <BookCover src={safePath} alt="历史封面大图" width={280}>
              <span>图片不可用或加载中</span>
            </BookCover>
            <div className="mono">{value}</div>
          </DialogBody>
        </Dialog>
      )}
    </div>
  );
}

/** 运维记录上下文单独展示，不混入书籍字段变更计数。 */
export function auditOperationContext(after: unknown): unknown {
  return asObject(after)?._operation;
}
export function auditOperationTimeLabel(createdAt: string, after: unknown): string {
  const operation = asObject(auditOperationContext(after));
  const interval = asObject(operation?.occurred_between);
  const hasInterval = typeof interval?.from === 'string' && typeof interval?.to === 'string';
  const occurredAt = typeof operation?.occurred_at === 'string' ? operation.occurred_at : createdAt;
  const time = hasInterval
    ? `${fmtDateTime(String(interval.from))}～${fmtDateTime(String(interval.to))}（范围）`
    : fmtDateTime(occurredAt);
  return operation?.backfilled === true ? `${time} · 补录` : time;
}

export function auditOperationSummary(after: unknown): string | undefined {
  const summary = asObject(auditOperationContext(after))?.summary;
  return typeof summary === 'string' ? summary : undefined;
}

/** 标量值友好化：① UUID→field_refs 业务名 + 小字短 id；② enum 码→FIELD_VALUE_LABELS 中文 + 小字原码；③ 否则原值截断。 */
function FriendlyScalar({
  value,
  field,
  refs,
}: {
  value: string | number;
  field?: string;
  refs?: Record<string, string> | null;
}) {
  if (field === 'cover_url' && typeof value === 'string')
    return <AuditCoverPreview value={value} />;
  const s = String(value);
  const refName = refs?.[s];
  const enumLabel = field ? FIELD_VALUE_LABELS[field]?.[s] : undefined;
  const friendly = refName ?? enumLabel;
  if (friendly) {
    return (
      <span style={{ wordBreak: 'break-word' }}>
        {friendly}
        <span className="mono" style={{ color: 'var(--fg-faint)', fontSize: 9.5, marginLeft: 5 }}>
          {refName ? s.slice(0, 8) : s}
        </span>
      </span>
    );
  }
  return <Truncatable text={s} />;
}

/**
 * diff 单元格值递归渲染（统一友好化，无裸露英文码 / 裸 UUID / true·false）：
 * - null/undefined → 『—』
 * - boolean → 通用「是/否」（FIELD_VALUE_LABELS[field] 可按字段覆盖）——一处覆盖所有布尔字段
 * - 数组 → 标量数组逐元素友好化（透传 field 给元素：enum 数组查 FIELD_VALUE_LABELS、UUID 数组查 refs）；
 *          对象数组逐项递归（卡片化），故级联快照 / 合并源标签等也友好显示
 * - 对象 → 逐 key 递归（key 经 FIELD_LABELS 友好化、值再递归），i18n / prefs / 嵌套快照通用
 * - 标量 → FriendlyScalar
 */
function AuditDiffValue({
  value,
  field,
  refs,
}: {
  value: unknown;
  field?: string;
  refs?: Record<string, string> | null;
}) {
  if (value === undefined || value === null)
    return <span style={{ color: 'var(--fg-faint)' }}>—</span>;
  if (typeof value === 'boolean') {
    const override = field ? FIELD_VALUE_LABELS[field]?.[String(value)] : undefined;
    return <span>{override ?? (value ? '是' : '否')}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span style={{ color: 'var(--fg-faint)' }}>—</span>;
    const allScalar = value.every((v) => v === null || typeof v !== 'object');
    if (allScalar) {
      // 标量数组：横向 wrap，逐元素透传 field（enum 数组 / UUID 数组皆友好）
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
          {value.map((v, i) => (
            <AuditDiffValue key={i} value={v} field={field} refs={refs} />
          ))}
        </div>
      );
    }
    // 对象数组：逐项卡片化递归
    return (
      <div className="vstack" style={{ gap: 6 }}>
        {value.map((v, i) => (
          <div key={i} style={{ paddingLeft: 8, borderLeft: '2px solid var(--border)' }}>
            <AuditDiffValue value={v} refs={refs} />
          </div>
        ))}
      </div>
    );
  }
  if (typeof value === 'object') {
    return (
      <div className="vstack" style={{ gap: 4 }}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 6 }}>
            <span style={{ color: 'var(--fg-faint)', flexShrink: 0, textDecoration: 'none' }}>
              {FIELD_LABELS[k] ?? k}:
            </span>
            <AuditDiffValue value={v} field={k} refs={refs} />
          </div>
        ))}
      </div>
    );
  }
  return <FriendlyScalar value={value as string | number} field={field} refs={refs} />;
}

/** 无字段级 diff 时按动作类型给兜底文案。 */
function noDiffNotice(type: AuditActionType): string {
  if (type === 'create') return '创建类操作 · 无前置状态可对比（新增记录）';
  if (type === 'export') return '导出类操作 · 不产生数据变更';
  if (type === 'login') return '登录事件 · 不产生数据变更';
  return '该操作未记录字段级 diff';
}

/**
 * 行内「变更摘要」：仅当恰好单个标量字段变更时回显 `标签 旧 → 新`（友好化同展开区）。
 * 多字段 / 对象值变更返回空串（完整 diff 在展开区）。值超 24 字截断。
 */
export function auditChangeSummary(
  diff: AuditFieldDiff[],
  refs?: Record<string, string> | null,
): string {
  const d = diff.length === 1 ? diff[0] : undefined;
  if (!d) return '';
  const friendly = (v: unknown): string | null => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean')
      return FIELD_VALUE_LABELS[d.field]?.[String(v)] ?? (v ? '是' : '否');
    if (typeof v === 'object') return null;
    const raw = String(v);
    return refs?.[raw] ?? FIELD_VALUE_LABELS[d.field]?.[raw] ?? raw;
  };
  const before = friendly(d.before);
  const after = friendly(d.after);
  if (before === null || after === null) return '';
  const cap = (s: string) => (s.length > 24 ? `${s.slice(0, 24)}…` : s);
  return `${FIELD_LABELS[d.field] ?? d.field} ${cap(before)} → ${cap(after)}`;
}

/**
 * 统一「字段变更 diff 表」：有 diff → 三列网格（字段名+原始 key / before 红 / after 绿，值经 AuditDiffValue 友好化）；
 * 无 diff → 按 action 类型显兜底文案。两处日志共用。
 * @param diff 调用方 deriveAuditDiff 后传入（行 badge / 摘要也复用同一 diff，避免重算）
 * @param fieldRefs 后端 field_refs（`*_id` 值→业务名）；book log 接入后即可用
 * @param action raw action code（无 diff 时据此 humanize 出类型给兜底文案）
 */
export function AuditDiffTable({
  diff,
  fieldRefs,
  action,
  operation,
}: {
  operation?: unknown;
  diff: AuditFieldDiff[];
  fieldRefs?: Record<string, string> | null;
  action: string;
}) {
  if (diff.length === 0) {
    return (
      <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', padding: '4px 0' }}>
        <Icons.IconInfo
          size={11}
          style={{ verticalAlign: 'middle', marginRight: 5, color: 'var(--info)' }}
        />
        {noDiffNotice(humanizeAuditAction(action).type)}
      </div>
    );
  }
  return (
    <div>
      {operation != null && (
        <div className="card-flat" style={{ marginBottom: 'var(--gap)' }}>
          <AuditDiffValue value={operation} />
        </div>
      )}
      <div className="label" style={{ marginBottom: 6 }}>
        字段变更 · before → after
      </div>
      <div
        className="vstack"
        style={{ gap: 0, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
      >
        {diff.map((c, ci) => (
          <div
            key={c.field}
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr 1fr',
              borderBottom: ci < diff.length - 1 ? '1px solid var(--border)' : 0,
              fontSize: 11.5,
            }}
          >
            <div
              style={{
                padding: '7px 10px',
                background: 'var(--bg-elev)',
                borderRight: '1px solid var(--border)',
              }}
            >
              <div style={{ fontWeight: 500 }}>{FIELD_LABELS[c.field] ?? c.field}</div>
              <div className="mono" style={{ fontSize: 9.5, color: 'var(--fg-faint)' }}>
                {c.field}
              </div>
            </div>
            <div
              className="mono"
              style={{
                padding: '7px 10px',
                color: 'var(--neg)',
                background: 'var(--neg-soft)',
                borderRight: '1px solid var(--border)',
                wordBreak: 'break-word',
              }}
            >
              <AuditDiffValue value={c.before} field={c.field} refs={fieldRefs} />
            </div>
            <div
              className="mono"
              style={{
                padding: '7px 10px',
                color: 'var(--pos)',
                background: 'var(--pos-soft)',
                wordBreak: 'break-word',
              }}
            >
              <AuditDiffValue value={c.after} field={c.field} refs={fieldRefs} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
